/**
 * FrequenciaVeiculos.js — Onda 108
 * CI Sistemas de Informática Ltda. — AcessoiD
 *
 * Frequência por veículo nas verticais Clube Social e Condomínio: quais placas
 * acessaram pelo menos N vezes num período, com exportação para planilha.
 *
 * São duas contagens, e elas respondem coisas diferentes: `acessos` é o número
 * de entradas (cinco idas num sábado são cinco acessos) e `dias` é o número de
 * dias distintos em que o veículo entrou (as mesmas cinco idas são um dia). O
 * seletor "Contar por" decide qual das duas o mínimo N compara — aqui o padrão
 * é `dias`, que é o que mede uso do espaço; no estacionamento, onde a tela
 * sempre contou estadias, o padrão continua sendo `acessos`.
 *
 * A saída não é somada aos acessos: ela tem coluna própria, porque o mesmo
 * carro saindo não é uma segunda visita.
 *
 * A consulta é explícita (botão Pesquisar), como nas demais telas de período:
 * o agrupamento por placa é caro em períodos longos.
 */
import React, { useState } from 'react';
import api from '../services/api';
import { hojeLocal } from '../utils/dataLocal'; // Onda 138: data local, não UTC
import { useAuth } from '../context/AuthContext';
import Pagination from './Pagination';
import { toast } from 'react-hot-toast';
import { FiCalendar, FiSearch, FiDownload, FiX, FiBarChart2 } from 'react-icons/fi';

const hojeISO = hojeLocal();
const primeiroDiaDoMes = `${hojeISO.slice(0, 7)}-01`;

const formatarDataHora = (d) => (d ? new Date(d).toLocaleString('pt-BR') : '—');

const FILTROS_INICIAIS = {
  start_date: primeiroDiaDoMes,
  end_date: hojeISO,
  min_visits: 2,
  base: 'dias',
  search: '',
  limit: 50,
  offset: 0,
};

export default function FrequenciaVeiculos({ ehClube = false }) {
  const { user } = useAuth();
  const podeExportar = user?.role === 'admin' || user?.role === 'superadmin';

  const [filtros, setFiltros] = useState(FILTROS_INICIAIS);
  const [resultado, setResultado] = useState(null);
  const [carregando, setCarregando] = useState(false);
  const [falhou, setFalhou] = useState(false);

  const trocar = (campo, valor) => setFiltros(f => ({ ...f, [campo]: valor }));

  const parametros = (f, comPaginacao = true) => {
    const p = new URLSearchParams({
      start_date: f.start_date,
      end_date: f.end_date,
      min_visits: String(f.min_visits),
      base: f.base,
    });
    if (f.search) p.append('search', f.search);
    if (comPaginacao) {
      p.append('limit', String(f.limit));
      p.append('offset', String(f.offset));
    }
    return p;
  };

  const pesquisar = async (f = filtros) => {
    setCarregando(true);
    setFalhou(false);
    try {
      const r = await api.get(`/api/reports/plate-frequency?${parametros(f).toString()}`);
      setResultado(r.data);
    } catch (e) {
      setFalhou(true);
      toast.error(e.response?.data?.error || 'Não foi possível levantar a frequência por veículo');
    } finally {
      setCarregando(false);
    }
  };

  // A consulta só roda no clique em "Pesquisar", depois de o gestor escolher o
  // filtro. Houve uma tentativa, em 23/09/2026, de consultar ao abrir a aba — o
  // Iatagã recusou com razão: a busca disparava antes de ele confirmar o que
  // queria. O que parecia "lista vazia" era, na verdade, a consulta levando 76 s
  // e o navegador desistindo; isso foi corrigido no servidor.

  const exportarCsv = async () => {
    try {
      const r = await api.get(
        `/api/reports/plate-frequency/export.csv?${parametros(filtros, false).toString()}`,
        { responseType: 'blob' }
      );
      const url = URL.createObjectURL(new Blob([r.data], { type: 'text/csv;charset=utf-8' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `frequencia_${filtros.start_date}_${filtros.end_date}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Não foi possível exportar o relatório');
    }
  };

  const itens = resultado?.items || [];
  const resumo = resultado?.summary || {};
  const total = resultado?.total || 0;
  const totalPaginas = Math.max(1, Math.ceil(total / filtros.limit));
  const paginaAtual = Math.floor(filtros.offset / filtros.limit) + 1;

  const irPara = (pagina) => {
    const alvo = Math.min(totalPaginas, Math.max(1, pagina));
    const f = { ...filtros, offset: (alvo - 1) * filtros.limit };
    setFiltros(f);
    pesquisar(f);
  };

  const trocarTamanho = (tamanho) => {
    const f = { ...filtros, limit: tamanho, offset: 0 };
    setFiltros(f);
    pesquisar(f);
  };

  const rotuloDoMinimo = filtros.base === 'dias' ? 'Mínimo de dias com acesso' : 'Mínimo de acessos';

  return (
    <div className="freq-veiculos">
      <div className="filters-card">
        <div className="filters-row">
          <div className="filter-group">
            <label><FiCalendar /> Data Inicial</label>
            <input type="date" value={filtros.start_date}
              onChange={e => trocar('start_date', e.target.value)} />
          </div>
          <div className="filter-group">
            <label><FiCalendar /> Data Final</label>
            <input type="date" value={filtros.end_date}
              onChange={e => trocar('end_date', e.target.value)} />
          </div>
          <div className="filter-group">
            <label>Contar por</label>
            <select value={filtros.base} onChange={e => trocar('base', e.target.value)}>
              <option value="dias">Dias com acesso</option>
              <option value="acessos">Acessos</option>
            </select>
          </div>
          <div className="filter-group">
            <label>{rotuloDoMinimo}</label>
            <input type="number" min="1" max="500" value={filtros.min_visits}
              onChange={e => trocar('min_visits', Math.max(1, Number(e.target.value) || 1))} />
          </div>
          <div className="filter-group search-filter">
            <label>Buscar placa</label>
            <div className="search-box">
              <FiSearch />
              <input placeholder="Parte da placa" value={filtros.search}
                onChange={e => trocar('search', e.target.value)} />
              {filtros.search && (
                <button onClick={() => trocar('search', '')} aria-label="Limpar busca"><FiX /></button>
              )}
            </div>
          </div>
          <div className="filter-group">
            <button className="btn btn-primary" disabled={carregando}
              onClick={() => { const f = { ...filtros, offset: 0 }; setFiltros(f); pesquisar(f); }}>
              {carregando ? 'Buscando…' : 'Pesquisar'}
            </button>
          </div>
          {podeExportar && itens.length > 0 && (
            <div className="filter-group">
              <button className="btn btn-secondary" onClick={exportarCsv}>
                <FiDownload /> Exportar CSV
              </button>
            </div>
          )}
        </div>
      </div>

      {resultado && (
        <div className="summary-cards">
          <div className="summary-card">
            <div className="summary-value">{resumo.placas || 0}</div>
            <div className="summary-label">Veículos no resultado</div>
          </div>
          <div className="summary-card info">
            <div className="summary-value">{resumo.acessos || 0}</div>
            <div className="summary-label">Acessos somados</div>
          </div>
          <div className="summary-card success">
            <div className="summary-value">{resumo.dias || 0}</div>
            <div className="summary-label">Dias com acesso somados</div>
          </div>
          <div className="summary-card">
            <div className="summary-value">{resumo.saidas || 0}</div>
            <div className="summary-label">Saídas lidas</div>
          </div>
        </div>
      )}

      {/* Duas situações diferentes, e a tela diz qual é: ainda não pesquisou,
          ou pesquisou e falhou. Misturar as duas é o que fazia a aba parecer
          vazia sem explicar por quê. */}
      {!resultado && !carregando && !falhou && (
        <div className="empty-state">
          <FiBarChart2 size={48} />
          <h3>Escolha o período e o mínimo</h3>
          <p>Depois clique em Pesquisar.</p>
        </div>
      )}
      {!resultado && !carregando && falhou && (
        <div className="empty-state">
          <FiBarChart2 size={48} />
          <h3>Não foi possível carregar</h3>
          <p>Tente de novo em Pesquisar. Se persistir, o período pode ser grande demais.</p>
        </div>
      )}

      {carregando && <div className="loading-state">Levantando a frequência...</div>}

      {resultado && !carregando && (
        <>
          <Pagination
            currentPage={paginaAtual}
            totalPages={totalPaginas}
            pageSize={filtros.limit}
            totalRecords={total}
            onPageChange={irPara}
            onPageSizeChange={trocarTamanho}
            itemLabel="veículo"
          />

          <div className="table-wrapper">
            <table className="report-table">
              <thead>
                <tr>
                  <th>Placa</th>
                  <th>{ehClube ? 'Associado' : 'Morador / Unidade'}</th>
                  <th>Acessos</th>
                  <th>Dias com acesso</th>
                  <th>Saídas</th>
                  <th>Primeiro acesso</th>
                  <th>Último acesso</th>
                  <th>Tipo</th>
                </tr>
              </thead>
              <tbody>
                {itens.length === 0 && (
                  <tr>
                    <td colSpan={8} className="empty-state">
                      Nenhum veículo atingiu <strong>{filtros.min_visits}</strong>
                      {filtros.base === 'dias' ? ' dia(s) com acesso' : ' acesso(s)'} entre{' '}
                      {filtros.start_date.split('-').reverse().join('/')} e{' '}
                      {filtros.end_date.split('-').reverse().join('/')}.
                      {filtros.search && <> A busca por <strong>{filtros.search}</strong> também está ativa.</>}
                    </td>
                  </tr>
                )}
                {itens.map(v => (
                  <tr key={v.plate}>
                    <td><span className="plate-mono">{v.plate}</span></td>
                    <td>{v.person || <span className="no-access-label">Não cadastrado</span>}</td>
                    <td className="text-center count-cell">{v.visits}</td>
                    <td className="text-center count-cell">{v.days}</td>
                    <td className="text-center">{v.exits}</td>
                    <td>{formatarDataHora(v.first_entry)}</td>
                    <td>{formatarDataHora(v.last_entry)}</td>
                    <td>{(v.access_types || []).join(', ') || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <Pagination
            currentPage={paginaAtual}
            totalPages={totalPaginas}
            pageSize={filtros.limit}
            totalRecords={total}
            onPageChange={irPara}
            onPageSizeChange={trocarTamanho}
            itemLabel="veículo"
          />
        </>
      )}
    </div>
  );
}
